import { useState, useRef, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Icons } from '@/shared/constants/icons';
import { ROUTES } from '@/shared/constants/routes';
import { TRANSITION, motionSafe } from '@/shared/constants/motion';
import { useNotifications, useMarkAllNotificationsRead } from '@/shared/hooks/useNotifications';
import { useOpenNotification } from '@/shared/hooks/useOpenNotification';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { Skeleton } from '@/shared/components/Skeleton';
import { NotificationRow } from '@/shared/components/NotificationRow';
import type { Notification } from '@/shared/services/notifications.service';

/** The badge shows a number up to this, then "9+". */
const BADGE_MAX = 9;

/**
 * The bell and its panel: the newest unread notifications from every brand
 * the reader is in, live through Supabase Realtime (useNotifications). An
 * item from another brand names it and switches to it on open. "View all"
 * opens the history (/notifications), where read ones stay.
 */
export function NotificationsMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduced = useReducedMotion() ?? false;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const currentWorkspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id ?? null);

  const { data, isLoading, isError, refetch } = useNotifications();
  const markAllRead = useMarkAllNotificationsRead();
  const openNotification = useOpenNotification();
  const notifications = data?.data ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  // Said once for a screen reader when new unread items arrive, never on load.
  const [announcement, setAnnouncement] = useState('');
  const lastCount = useRef<number | null>(null);
  useEffect(() => {
    if (!data) return;
    if (lastCount.current !== null && unreadCount > lastCount.current) {
      setAnnouncement(t('topbar.notificationsUnread', { count: unreadCount }));
    }
    lastCount.current = unreadCount;
  }, [data, unreadCount, t]);

  const close = (restoreFocus: boolean): void => {
    setOpen(false);
    if (restoreFocus) bellRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close(true);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Opening moves focus into the panel: the first item, else the panel itself.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>('[data-testid="notification-row"], [data-autofocus]');
      (first ?? panelRef.current)?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, isLoading]);

  const openItem = (n: Notification): void => {
    openNotification(n);
    close(false);
  };

  const badge = unreadCount > BADGE_MAX ? `${BADGE_MAX}+` : String(unreadCount);

  return (
    <div ref={ref} className="relative">
      <button
        ref={bellRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative w-11 h-11 rounded-full bg-transparent border-none cursor-pointer flex items-center justify-center hover:bg-[var(--color-surface-3)]"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={unreadCount > 0 ? t('topbar.notificationsUnread', { count: unreadCount }) : t('topbar.notifications')}
      >
        <Icons.notification size={20} className="text-[var(--ink-2)]" strokeWidth={1.5} aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            className="absolute top-1 end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--color-accent)] text-white text-[11px] leading-[14px] font-semibold flex items-center justify-center"
            style={{ border: '2px solid var(--color-surface)' }}
            aria-hidden="true"
            data-testid="notification-badge"
          >
            {badge}
          </span>
        )}
      </button>
      <span className="sr-only" role="status" aria-live="polite">{announcement}</span>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={t('topbar.notifications')}
            tabIndex={-1}
            initial={reduced ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -4 }}
            transition={motionSafe(TRANSITION.panel, reduced)}
            className="fixed inset-x-3 top-[64px] sm:absolute sm:inset-x-auto sm:end-0 sm:top-full mt-1.5 sm:w-[360px] bg-[var(--color-surface)] border border-[var(--line)] rounded-2xl overflow-hidden z-[100] flex flex-col outline-none"
            style={{ boxShadow: 'var(--shadow-pop)', maxHeight: 'min(480px, calc(100dvh - 80px))' }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--line)]">
              <span className="text-body font-semibold text-[var(--ink)]">{t('topbar.notifications')}</span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead.mutate(undefined)}
                  className="text-meta text-[var(--color-accent-text)] font-semibold bg-transparent border-none cursor-pointer hover:underline min-h-[32px]"
                >
                  {t('topbar.markAllRead')}
                </button>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {isLoading ? (
                <div className="p-4 space-y-4" aria-busy="true">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex gap-3">
                      <Skeleton width={16} height={16} rounded="sm" />
                      <div className="flex-1 space-y-2">
                        <Skeleton height={12} width="70%" rounded="sm" />
                        <Skeleton height={10} width="90%" rounded="sm" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : isError ? (
                <div className="py-8 px-4 text-center" role="alert">
                  <p className="text-ui text-[var(--ink-2)] m-0 mb-3">{t('notifications.center.loadFailed')}</p>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => void refetch()} data-autofocus="">
                    {t('common.retry')}
                  </button>
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-8 px-4 text-center text-ui text-[var(--ink-3)]">{t('notifications.center.caughtUp')}</div>
              ) : (
                notifications.map((n) => (
                  <NotificationRow key={n.id} notification={n} onOpen={openItem} currentWorkspaceId={currentWorkspaceId} />
                ))
              )}
            </div>
            <div className="border-t border-[var(--line)]">
              <button
                type="button"
                onClick={() => {
                  close(false);
                  navigate(ROUTES.NOTIFICATIONS);
                }}
                className="w-full min-h-[44px] text-ui font-semibold text-[var(--color-accent-text)] bg-transparent border-none cursor-pointer hover:bg-[var(--color-surface-2)]"
              >
                {t('notifications.center.viewAll')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
